import { createPillButtonGroup } from '../ui/pillButtonGroup.js';
import { createMultirxShineLoader } from '../ui/multirxShineLoader.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import { mountCarOrientationEmbed, type CarOrientationEmbedHandle } from './carOrientationEmbed.js';
import { rotateCarModel3d, type Car3dModelItem } from './api.js';

/**
 * Montages live simultanés avant snapshot.
 * Dès qu’un aperçu est figé, le slot est libéré → la file avance (plus de
 * « Chargement… » bloqué derrière 8 WebGL qui ne se libèrent jamais).
 */
export const MAX_CONCURRENT_ORIENTATION_EMBEDS = 3;

/** Délai de base avant retry après échec / context lost (backoff linéaire). */
export const ORIENTATION_EMBED_RETRY_BASE_MS = 800;
export const ORIENTATION_EMBED_MAX_RETRIES = 4;

/** Après avoir quitté les boutons de rotation, délai avant de re-figer / libérer le WebGL. */
export const PREVIEW_IDLE_UNLOAD_MS = 3000;

const ROTATION_STEPS: ReadonlyArray<90 | 180 | 270> = [90, 180, 270];

type MountMode = 'initial' | 'preview' | 'rotate';

let activeEmbeds = 0;
const embedQueue: Array<() => void> = [];

function scheduleOrientationEmbed(start: () => void): () => void {
  let started = false;
  let cancelled = false;
  const task = (): void => {
    if (cancelled) return;
    started = true;
    activeEmbeds++;
    start();
  };
  if (activeEmbeds < MAX_CONCURRENT_ORIENTATION_EMBEDS) task();
  else embedQueue.push(task);
  return () => {
    if (started) return;
    cancelled = true;
    const i = embedQueue.indexOf(task);
    if (i !== -1) embedQueue.splice(i, 1);
  };
}

function releaseOrientationEmbedSlot(): void {
  activeEmbeds = Math.max(0, activeEmbeds - 1);
  const next = embedQueue.shift();
  next?.();
}

function showWaitingLoader(host: HTMLElement, label = 'Chargement du modèle…'): void {
  host.classList.add('car-orientation-embed');
  const viewport = document.createElement('div');
  viewport.className = 'car-orientation-embed__viewport';
  viewport.appendChild(createMultirxShineLoader(label));
  host.replaceChildren(viewport);
}

function clearWaitingShell(host: HTMLElement): void {
  host.replaceChildren();
  host.classList.remove('car-orientation-embed');
}

function captureViewportPng(host: HTMLElement): string | null {
  const canvas = host.querySelector('canvas');
  if (!(canvas instanceof HTMLCanvasElement)) return null;
  try {
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl.startsWith('data:image/') ? dataUrl : null;
  } catch {
    return null;
  }
}

/**
 * Monte l’embed en lazy : file WebGL courte → rendu → snapshot PNG → libération
 * du contexte. Survol du groupe de rotations = remount live (fantôme) puis
 * re-snapshot au leave. Clic rotation = API + remount + snapshot.
 */
export function mountLazyCarOrientationEmbed(
  host: HTMLElement,
  item: Car3dModelItem,
  root?: Element | null
): () => void {
  let handle: CarOrientationEmbedHandle | null = null;
  let cancelQueued: (() => void) | null = null;
  let visible = false;
  let disposed = false;
  let slotHeld = false;
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let contextLostBound: ((e: Event) => void) | null = null;
  let readyWatchGen = 0;
  let snapshotted = false;
  let snapshotUrl: string | null = null;
  let rotating = false;
  let hoverPreview = false;
  let liveReady = false;
  let mountMode: MountMode = 'initial';
  let unloadTimer: ReturnType<typeof setTimeout> | null = null;

  const clearRetryTimer = (): void => {
    if (retryTimer == null) return;
    clearTimeout(retryTimer);
    retryTimer = null;
  };

  const clearUnloadTimer = (): void => {
    if (unloadTimer == null) return;
    clearTimeout(unloadTimer);
    unloadTimer = null;
  };

  const finishSlot = (): void => {
    if (!slotHeld) return;
    slotHeld = false;
    cancelQueued = null;
    releaseOrientationEmbedSlot();
  };

  const detachContextLost = (): void => {
    if (!contextLostBound) return;
    const canvas = host.querySelector('canvas');
    canvas?.removeEventListener('webglcontextlost', contextLostBound);
    contextLostBound = null;
  };

  const disposeLive = (): void => {
    detachContextLost();
    readyWatchGen += 1;
    liveReady = false;
    handle?.dispose();
    handle = null;
  };

  const restoreFrozenShell = (statusMessage?: string): void => {
    if (!snapshotUrl) return;
    snapshotted = true;
    renderFrozenShell(snapshotUrl, statusMessage);
  };

  const freezeLiveToSnapshot = (statusMessage?: string): void => {
    if (!handle) return;
    const gen = ++readyWatchGen;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (disposed || gen !== readyWatchGen) return;
        const dataUrl = captureViewportPng(host) ?? snapshotUrl;
        disposeLive();
        finishSlot();
        mountMode = 'initial';
        if (!dataUrl) {
          scheduleRetry('fail');
          return;
        }
        attempt = 0;
        snapshotted = true;
        snapshotUrl = dataUrl;
        renderFrozenShell(dataUrl, statusMessage);
      });
    });
  };

  const renderFrozenShell = (dataUrl: string, statusMessage?: string): void => {
    host.classList.add('car-orientation-embed');
    const viewport = document.createElement('div');
    viewport.className = 'car-orientation-embed__viewport';
    const img = document.createElement('img');
    img.className = 'car-orientation-embed__snapshot';
    img.alt = '';
    img.src = dataUrl;
    viewport.appendChild(img);

    const actionsEl = document.createElement('div');
    actionsEl.className = 'car-orientation-embed__actions';
    const rotateGroup = createPillButtonGroup(
      ROTATION_STEPS.map((degrees) => ({
        label: `↻ ${degrees}°`,
        variant: 'soft' as const,
        onClick: () => void commitFrozenRotation(degrees),
      })),
      { small: true, ariaLabel: 'Rotation du modèle' }
    );
    for (const btn of rotateGroup.querySelectorAll<HTMLElement>('.btn-pill')) {
      btn.dataset.role = 'admin,moderator';
    }
    attachPreviewHover(rotateGroup);
    actionsEl.appendChild(rotateGroup);

    const statusEl = document.createElement('p');
    statusEl.className = 'car-orientation-embed__status';
    statusEl.setAttribute('role', 'status');
    statusEl.textContent = statusMessage ?? `Modèle : ${item.slug}`;

    host.replaceChildren(viewport, actionsEl, statusEl);
    applyRoleVisibility();
  };

  const attachPreviewHover = (el: HTMLElement): void => {
    el.addEventListener('mouseenter', onPreviewEnter);
    el.addEventListener('mouseleave', onPreviewLeave);
  };

  const onPreviewEnter = (): void => {
    if (disposed || rotating || !visible) return;
    clearUnloadTimer();
    hoverPreview = true;
    // Déjà live (preview en cours) : rien à faire, le fantôme marche.
    if (handle || cancelQueued) return;
    if (!snapshotted) return;
    wakeLiveForPreview();
  };

  const onPreviewLeave = (): void => {
    hoverPreview = false;
    if (rotating || disposed) return;
    clearUnloadTimer();
    unloadTimer = setTimeout(() => {
      unloadTimer = null;
      if (disposed || rotating || hoverPreview) return;
      if (handle) {
        if (liveReady) freezeLiveToSnapshot();
        // sinon watchReady figera dès que ready + !hoverPreview
        return;
      }
      // Encore en file : annuler et garder le snapshot
      cancelQueued?.();
      cancelQueued = null;
      if (snapshotUrl) restoreFrozenShell();
    }, PREVIEW_IDLE_UNLOAD_MS);
  };

  const scheduleRetry = (reason: 'fail' | 'context'): void => {
    if (disposed || !visible || snapshotted) return;
    if (attempt >= ORIENTATION_EMBED_MAX_RETRIES) {
      showWaitingLoader(host, 'Échec du chargement');
      return;
    }
    attempt += 1;
    clearRetryTimer();
    const delay = ORIENTATION_EMBED_RETRY_BASE_MS * attempt;
    showWaitingLoader(
      host,
      reason === 'context'
        ? `Reprise WebGL dans ${Math.round(delay / 1000)}s…`
        : `Nouvel essai dans ${Math.round(delay / 1000)}s…`
    );
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (disposed || !visible || snapshotted) return;
      startMount('initial');
    }, delay);
  };

  const watchReady = (mounted: CarOrientationEmbedHandle, mode: MountMode): void => {
    const gen = ++readyWatchGen;
    void mounted.ready.then((ok) => {
      if (disposed || gen !== readyWatchGen || handle !== mounted) return;
      if (!ok) {
        disposeLive();
        finishSlot();
        if (mode === 'preview' && snapshotUrl) {
          restoreFrozenShell('Impossible de charger la prévisualisation.');
          return;
        }
        scheduleRetry('fail');
        return;
      }
      liveReady = true;
      attempt = 0;
      if (mode === 'preview') {
        // Reste live tant que le curseur est sur le groupe ; sinon fige.
        if (!hoverPreview) freezeLiveToSnapshot();
        return;
      }
      freezeLiveToSnapshot(
        mode === 'rotate' ? `« ${item.slug} » — rotation enregistrée.` : undefined
      );
    });
  };

  const wakeLiveForPreview = (): void => {
    if (disposed || !visible || handle || cancelQueued || retryTimer != null || rotating) {
      return;
    }
    startMount('preview');
  };

  const commitFrozenRotation = async (degrees: 90 | 180 | 270): Promise<void> => {
    if (disposed || rotating) return;
    rotating = true;
    hoverPreview = false;
    clearUnloadTimer();
    const previousUrl = snapshotUrl;
    try {
      showWaitingLoader(host, `Rotation de ${degrees}°…`);
      snapshotted = false;
      snapshotUrl = null;
      await rotateCarModel3d(item.slug, degrees);
      if (disposed) return;
      startMount('rotate');
      if (!handle && !cancelQueued && previousUrl) {
        // Slot indisponible / hors écran : restaure l’ancien aperçu
        snapshotUrl = previousUrl;
        restoreFrozenShell();
      }
    } catch (err) {
      if (previousUrl) {
        snapshotUrl = previousUrl;
        restoreFrozenShell(err instanceof Error ? err.message : 'Erreur lors de la rotation.');
      } else {
        scheduleRetry('fail');
      }
    } finally {
      rotating = false;
    }
  };

  const startMount = (mode: MountMode): void => {
    if (disposed || !visible || handle || cancelQueued || retryTimer != null) return;
    if (mode === 'initial' && snapshotted) return;

    mountMode = mode;
    if (mode !== 'preview') {
      showWaitingLoader(
        host,
        mode === 'rotate' ? 'Enregistrement de la rotation…' : 'Chargement du modèle…'
      );
      snapshotted = false;
    }

    cancelQueued = scheduleOrientationEmbed(() => {
      cancelQueued = null;
      if (disposed || !visible) {
        releaseOrientationEmbedSlot();
        return;
      }
      if (mode === 'preview' && !hoverPreview) {
        releaseOrientationEmbedSlot();
        if (snapshotUrl) restoreFrozenShell();
        return;
      }
      if (mode === 'initial' && snapshotted) {
        releaseOrientationEmbedSlot();
        return;
      }

      slotHeld = true;
      liveReady = false;
      snapshotted = false;
      try {
        handle = mountCarOrientationEmbed(host, item);
      } catch {
        finishSlot();
        if (mode === 'preview' && snapshotUrl) {
          restoreFrozenShell();
          return;
        }
        scheduleRetry('fail');
        return;
      }

      contextLostBound = (e: Event): void => {
        e.preventDefault();
        const wasPreview = mountMode === 'preview';
        disposeLive();
        finishSlot();
        if (wasPreview && snapshotUrl) {
          restoreFrozenShell();
          return;
        }
        scheduleRetry('context');
      };
      const canvas = host.querySelector('canvas');
      canvas?.addEventListener('webglcontextlost', contextLostBound);
      watchReady(handle, mode);
      // Le montage live remplace le DOM : rattacher le hover sur le nouveau groupe.
      if (mode === 'preview') {
        const liveGroup = host.querySelector<HTMLElement>(
          '.car-orientation-embed__actions .btn-pill-group, .car-orientation-embed__actions'
        );
        if (liveGroup) attachPreviewHover(liveGroup);
      }
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        visible = entry.isIntersecting;
        if (visible) {
          if (!snapshotted) startMount('initial');
        } else {
          hoverPreview = false;
          clearUnloadTimer();
          cancelQueued?.();
          cancelQueued = null;
          clearRetryTimer();
          if (handle) {
            disposeLive();
            finishSlot();
          }
          if (!disposed && !snapshotted && !snapshotUrl) showWaitingLoader(host);
          else if (!disposed && snapshotUrl && !snapshotted) restoreFrozenShell();
        }
      }
    },
    {
      root: root ?? null,
      rootMargin: root ? '0px' : '80px',
    }
  );
  observer.observe(host);

  return () => {
    disposed = true;
    hoverPreview = false;
    observer.disconnect();
    cancelQueued?.();
    cancelQueued = null;
    clearRetryTimer();
    clearUnloadTimer();
    disposeLive();
    finishSlot();
    clearWaitingShell(host);
  };
}
